(() => {
  /* TSR Session 1 — trigger + suppression rework.
     Was: auto-open 3s after load, sessionStorage (session-only) dismissal.
     Now: first-of {6s dwell, 50% scroll, desktop exit-intent}; 14-day localStorage
     suppression; skip for subscribers / on cart+checkout / builder-in-progress /
     while the cart-notification is open. Escape + focus-trap + "No thanks" dismiss.
     The falling-salt engine (createSalt) is unchanged from the DEV redesign.
     Follow-up (post-launch tuning): dwell 25s->6s, suppress 30d->14d, and the
     storage key was versioned (-v2) so stale 30-day dismissals reset cleanly.
     2026-07-04 sparkle tuning (Koby): wider grain size/speed/opacity variance,
     rare larger grains, gentle sine sway while falling, more frequent glints
     with occasional twin-bursts, slightly longer flashes. Matches the
     tsr-sparkle-plus.js overlay params used on the section salt hosts.
     2026-08-13 audit Tier 1.2: the 6s dwell timer was removed. It always won the
     race, so the popup opened unconditionally ~6.5s after load — full-screen on
     mobile, on top of the hero. Triggers are now engagement-only: desktop
     exit-intent, plus 50% scroll on every device (the mobile trigger). Someone who
     never scrolls and never leaves simply never sees it. Revert: restore DWELL_MS
     and the dwellTimer/clearTimeout lines. */
  const DISMISS_KEY = 'tsr-nlpopup:dismissed-until-v2';
  const SUPPRESS_MS = 14 * 24 * 60 * 60 * 1000;
  const SCROLL_FRAC = 0.5;
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const DESK = window.matchMedia('(min-width: 990px)');
  const FINE = window.matchMedia('(pointer: fine)');

  const now = () => Date.now();

  const setDismissed = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(now() + SUPPRESS_MS));
    } catch (error) {
      // no-op if storage is unavailable
    }
  };

  const isSuppressed = () => {
    try {
      if (window.TSR_SUBSCRIBED === true) return true;
    } catch (error) {
      // ignore
    }
    try {
      const until = parseInt(window.localStorage.getItem(DISMISS_KEY) || '0', 10);
      if (until && now() < until) return true;
    } catch (error) {
      // ignore
    }
    return false;
  };

  // Never interrupt the commitment moments.
  const inBlockedContext = () => {
    const path = (window.location.pathname || '').toLowerCase();
    if (path.indexOf('/cart') === 0 || path.indexOf('/checkout') !== -1) return true;
    const bbCount = document.querySelector('[data-tsr-bundle-builder] [data-bb-count]');
    if (bbCount && parseInt(bbCount.textContent || '0', 10) > 0) return true;
    if (document.querySelector('cart-notification.active, #cart-notification.active, .cart-notification.active')) {
      return true;
    }
    return false;
  };

  /* --- Falling salt --------------------------------------------------
     Mirrors the initSaltDrift engine in tsr-interactions.js (footer CTA,
     reviews, product features) so the popup card gets the exact same
     drifting, twinkling grains. Skipped under prefers-reduced-motion —
     the CSS falls back to static speckles instead. */
  const createSalt = (host) => {
    if (REDUCED.matches) return null;

    const canvas = document.createElement('canvas');
    canvas.className = 'newsletter-popup__salt-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    host.insertBefore(canvas, host.firstChild);

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      canvas.remove();
      return null;
    }

    const bg = getComputedStyle(host).backgroundColor.match(/\d+/g) || [255, 255, 255];
    const lum = (0.299 * bg[0] + 0.587 * bg[1] + 0.114 * bg[2]) / 255;
    const dark = lum < 0.5;
    const color = dark ? '#EDE5DC' : '#42311F';
    const glintCore = dark ? '#FFFDF7' : '#FFFFFF';
    const mobile = !DESK.matches;

    let W;
    let H;
    let raf = null;
    let running = false;
    let grains = [];
    let nextSpark = 12 + Math.random() * 48;

    const size = () => {
      W = canvas.width = host.clientWidth;
      H = canvas.height = host.clientHeight;
    };

    const reset = (g, top) => {
      g.x = Math.random() * W;
      g.y = top ? -4 : Math.random() * H;
      g.s = 1.1 + Math.random() * 2.6;
      if (Math.random() < 0.06) g.s += 1.2;
      g.v = 0.12 + Math.random() * 0.4;
      g.dx = (Math.random() - 0.5) * 0.14;
      g.o = 0.15 + Math.random() * 0.27;
      g.ph = Math.random() * 6.283;
      g.ga = Math.random() * 1.57;
      g.fl = 0;
    };

    const build = () => {
      grains = [];
      const n = Math.min(mobile ? 30 : 60, Math.round(W / (mobile ? 30 : 26)));
      for (let i = 0; i < n; i++) {
        const g = {};
        reset(g, false);
        grains.push(g);
      }
    };

    const drawGlint = (cx, cy, s, k, a) => {
      const reach = s * (1.3 + k * 2);
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(a);
      if (dark) {
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = 0.06 * k;
        ctx.fillStyle = '#F5D9A6';
        ctx.beginPath();
        ctx.arc(0, 0, reach * 0.85, 0, 6.283);
        ctx.fill();
      }
      ctx.globalAlpha = (dark ? 0.3 : 0.2) * k;
      ctx.fillStyle = glintCore;
      ctx.beginPath();
      ctx.moveTo(0, -reach);
      ctx.lineTo(s * 0.45, 0);
      ctx.lineTo(0, reach);
      ctx.lineTo(-s * 0.45, 0);
      ctx.closePath();
      ctx.moveTo(-reach, 0);
      ctx.lineTo(0, s * 0.45);
      ctx.lineTo(reach, 0);
      ctx.lineTo(0, -s * 0.45);
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = (dark ? 0.55 : 0.38) * k;
      ctx.beginPath();
      ctx.arc(0, 0, 0.7 + k * 1.2, 0, 6.283);
      ctx.fill();
      ctx.restore();
    };

    const step = () => {
      if (!running) return;
      ctx.clearRect(0, 0, W, H);
      if (grains.length && --nextSpark <= 0) {
        const sg = grains[(Math.random() * grains.length) | 0];
        sg.fd = (24 + Math.pow(Math.random(), 2) * 168) | 0;
        sg.fl = sg.fd;
        sg.fk = 0.45 + Math.random() * 0.75;
        nextSpark = 40 + Math.random() * 44;
        if (Math.random() < 0.18) nextSpark = 6 + Math.random() * 10;
      }
      for (let i = 0; i < grains.length; i++) {
        const g = grains[i];
        g.y += g.v;
        g.x += g.dx + Math.sin((g.y + g.ph * 97) * 0.018) * 0.09;
        if (g.y > H + 4) reset(g, true);
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = g.o;
        ctx.fillStyle = color;
        ctx.fillRect(g.x, g.y, g.s, g.s);
        if (g.fl > 0) {
          const p = (g.fd - g.fl) / g.fd;
          drawGlint(g.x + g.s / 2, g.y + g.s / 2, g.s, g.fk * Math.sin(p * 3.14159), g.ga);
          g.fl--;
        }
      }
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      raf = window.requestAnimationFrame(step);
    };

    window.addEventListener('resize', () => {
      size();
      build();
    });

    return {
      start() {
        if (running) return;
        size();
        build();
        running = true;
        step();
      },
      stop() {
        running = false;
        if (raf) {
          window.cancelAnimationFrame(raf);
          raf = null;
        }
      }
    };
  };

  const initPopup = () => {
    const root = document.querySelector('[data-newsletter-popup-root]');
    if (!root || root.dataset.popupReady === 'true') return;

    root.dataset.popupReady = 'true';

    const dialog = root.querySelector('.newsletter-popup__dialog');
    const overlay = root.querySelector('[data-newsletter-popup-overlay]');
    const closeButton = root.querySelector('[data-newsletter-popup-close]');
    const dismissLink = root.querySelector('[data-newsletter-popup-dismiss]');
    const hasSuccess = Boolean(root.querySelector('[data-newsletter-popup-success="true"]'));
    const hasError = Boolean(root.querySelector('[data-newsletter-popup-error="true"]'));

    let isOpen = false;
    let salt = null;
    let lastFocus = null;

    const focusables = () =>
      Array.from(
        root.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null);

    const onKeydown = (event) => {
      if (!isOpen) return;
      const key = (event.key || '').toLowerCase();
      if (key === 'escape') {
        closePopup(true);
        return;
      }
      if (event.key === 'Tab') {
        const f = focusables();
        if (f.length === 0) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    const openPopup = (force) => {
      if (isOpen) return false;
      if (!force && (isSuppressed() || inBlockedContext())) return false;

      lastFocus = document.activeElement;
      root.hidden = false;
      document.body.classList.add('newsletter-popup-open');
      isOpen = true;

      if (!salt && dialog) salt = createSalt(dialog);
      if (salt) salt.start();

      document.addEventListener('keydown', onKeydown);

      window.requestAnimationFrame(() => {
        const focusTarget =
          root.querySelector('input[type="email"]') ||
          root.querySelector('button[type="submit"]') ||
          dialog;
        if (focusTarget) {
          focusTarget.focus({ preventScroll: true });
        }
      });
      return true;
    };

    const closePopup = (persist) => {
      if (!isOpen) return;
      if (persist) setDismissed();

      if (salt) salt.stop();

      root.hidden = true;
      document.body.classList.remove('newsletter-popup-open');
      isOpen = false;

      document.removeEventListener('keydown', onKeydown);

      if (lastFocus && typeof lastFocus.focus === 'function') {
        try {
          lastFocus.focus({ preventScroll: true });
        } catch (error) {
          // ignore
        }
      }
    };

    if (closeButton) closeButton.addEventListener('click', () => closePopup(true));
    if (overlay) overlay.addEventListener('click', () => closePopup(true));
    if (dismissLink) dismissLink.addEventListener('click', () => closePopup(true));

    /* 2026-09-02: the popup used to spring open after ANY successful newsletter
       signup on the page, including the footer one. form.posted_successfully? is
       a PAGE-level flag in Liquid, not per-form, so the popup's own success block
       renders even when the footer form was the one submitted, and hasSuccess went
       true. Subscribing at the bottom of a page therefore threw a modal at you,
       which reads as though the first attempt had not worked.

       Shopify returns to the anchor of the form actually submitted
       (/contact#NewsletterPopupForm vs /contact#contact_form), so that tells them
       apart. A sessionStorage flag set on submit is carried as a backup in case a
       client drops the fragment, otherwise a popup signup would show no
       confirmation at all. Either way setDismissed() runs, so someone who has just
       subscribed is never asked again. */
    const POPUP_SUBMIT_KEY = 'tsr-nlpopup:submitted';
    const popupForm = root.querySelector('form');
    if (popupForm) {
      popupForm.addEventListener('submit', () => {
        try { window.sessionStorage.setItem(POPUP_SUBMIT_KEY, '1'); } catch (error) { /* no-op */ }
      });
    }
    const submittedFromPopup = (() => {
      if (window.location.hash === '#NewsletterPopupForm') return true;
      try {
        if (window.sessionStorage.getItem(POPUP_SUBMIT_KEY) === '1') return true;
      } catch (error) { /* no-op */ }
      return false;
    })();
    const clearPopupSubmitFlag = () => {
      try { window.sessionStorage.removeItem(POPUP_SUBMIT_KEY); } catch (error) { /* no-op */ }
    };

    if (hasSuccess) {
      setDismissed();
      if (submittedFromPopup) openPopup(true);
      clearPopupSubmitFlag();
      return;
    }
    if (hasError) {
      if (submittedFromPopup) openPopup(true);
      clearPopupSubmitFlag();
      return;
    }
    clearPopupSubmitFlag();

    // Already dismissed (or subscribed): never arm the triggers.
    if (isSuppressed()) return;

    // Grace period so load-time scroll-restoration / mouseout artifacts can't open it.
    const armAt = now() + 1500;
    let userMoved = false;
    document.addEventListener('mousemove', () => { userMoved = true; }, { passive: true });

    let triggered = false;
    let cleanup = function () {};

    const fire = () => {
      if (triggered) return;
      if (openPopup(false)) {
        triggered = true;
        cleanup();
      }
    };

    // 50% scroll — the mobile trigger, and a secondary desktop one.
    const onScroll = () => {
      if (now() < armAt) return;
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      if ((window.scrollY || window.pageYOffset) / scrollable >= SCROLL_FRAC) fire();
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    // Exit intent is desktop-only (no reliable mobile equivalent).
    let onMouseOut = null;
    if (DESK.matches && FINE.matches) {
      onMouseOut = (event) => {
        if (now() < armAt || !userMoved) return;
        if (event.clientY <= 0 && !event.relatedTarget && !event.toElement) fire();
      };
      document.addEventListener('mouseout', onMouseOut);
    }

    cleanup = function () {
      window.removeEventListener('scroll', onScroll);
      if (onMouseOut) document.removeEventListener('mouseout', onMouseOut);
    };
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPopup);
  } else {
    initPopup();
  }
})();
